import {
    useState, useRef, useEffect, useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTruck } from '@fortawesome/free-solid-svg-icons';
import { getDaysSinceWeekStart } from '@s20/common/util/date';
import { useCalendarWeekStartsOn } from '@s20/hooks/tenant.hooks';
import JobCard from './JobCard';


// Custom event component for Week View - shows as a compact header
const WeekViewEvent = ({ event }) => {
    const weekStartsOn = useCalendarWeekStartsOn();
    // title and children props from react-big-calendar are ignored
    const [showPopover, setShowPopover] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const headerRef = useRef(null);
    const timeoutRef = useRef(null);

    const job = event.resource || event;
    const isGrouped = job.isGrouped === true;
    // Week view: compare against current time (not just day)
    const isPastJob = moment(event.start).isBefore(moment());

    // For grouped events, use a neutral color
    const jobTypeColor = isGrouped ? '#6c757d' : (job.jobTypeColor || job.resource?.jobTypeColor || '#9ca3af');

    const handleMouseEnter = () => {
        clearTimeout(timeoutRef.current);
        setShowPopover(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setShowPopover(false);
        }, 100);
    };

    const handlePopoverMouseEnter = () => {
        clearTimeout(timeoutRef.current);
    };

    const handlePopoverMouseLeave = () => {
        setShowPopover(false);
    };

    useEffect(() => () => {
            clearTimeout(timeoutRef.current);
        }, [timeoutRef]);

    useLayoutEffect(() => {
        if (showPopover && headerRef.current) {
            const rect = headerRef.current.getBoundingClientRect();
            const popoverWidth = rect.width * 1.2;
            const leftOffset = rect.width * 0.1;
            setPopoverStyle({
                position: 'fixed',
                left: `${rect.left - leftOffset}px`,
                top: `${rect.top}px`,
                width: `${popoverWidth}px`,
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1008,
            });
        }
    }, [showPopover]);

    // If not grouped (single job), show JobCard directly
    if (!isGrouped) {
        // The first column of the tenant week gets its chip on the right
        const chipPosition = getDaysSinceWeekStart(event.start, weekStartsOn) === 0 ? 'right' : 'left';

        return (
            <div className="week-view-event-container">
                <JobCard
                    job={job}
                    onClick={() => {/* handle click if needed */}}
                    chipPosition={chipPosition}
                    isPreviousDay={isPastJob}
                    inPopover={false}
                    currentView="week"
                />
            </div>
        );
    }

    // Build CSS classes
    const headerClasses = [
        'week-view-event-header',
        isPastJob && 'week-view-event-header--past',
    ].filter(Boolean).join(' ');

    // Grouped header display for Week View (multiple jobs)
    return (
        <>
            <div
                ref={headerRef}
                className={headerClasses}
                style={{ '--job-type-color': jobTypeColor }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="week-view-event-header__jobs">
                    <span className="job-count">
                        {`${job.jobCount} jobs`}
                    </span>
                </div>
                <div className="week-view-event-header__stats">
                    {job.totalStaff > 0 && (
                        <div className="week-view-event-header__stats__item">
                            <FontAwesomeIcon icon={faUsers} />
                            {job.totalStaff}
                        </div>
                    )}
                    {job.totalVehicles > 0 && (
                        <div className="week-view-event-header__stats__item">
                            <FontAwesomeIcon icon={faTruck} />
                            {job.totalVehicles}
                        </div>
                    )}
                </div>
            </div>

            {/* Popover with full job card(s) - positioned over the header */}
            {showPopover && ReactDOM.createPortal(
                <div
                    className="calendar-popover"
                    style={popoverStyle}
                    onMouseEnter={handlePopoverMouseEnter}
                    onMouseLeave={handlePopoverMouseLeave}
                >
                    {isGrouped ? (
                        <div>
                            <div className="calendar-popover__header">
                                {job.jobCount} job{job.jobCount !== 1 ? 's' : ''}
                            </div>
                            <div className="calendar-popover__jobs">
                                {job.groupedEvents.map((groupedEvent, idx) => (
                                    <JobCard
                                        key={groupedEvent.id || idx}
                                        job={groupedEvent.resource}
                                        onClick={() => setShowPopover(false)}
                                        chipPosition="left"
                                        isPreviousDay={isPastJob}
                                        inPopover
                                        currentView="week"
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <JobCard
                            job={job}
                            onClick={() => setShowPopover(false)}
                            chipPosition="left"
                            isPreviousDay={isPastJob}
                            inPopover
                            currentView="week"
                        />
                    )}
                </div>,
                document.body,
            )}
        </>
    );
};

export default WeekViewEvent;
